import { Head, Link } from "@inertiajs/react"
import {
  Activity,
  AlertTriangle,
  Calendar,
  Eye,
  Flag,
  MessageSquare,
  Shield,
  ShoppingBag,
  Trophy,
  Users,
} from "lucide-react"

import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import AppLayout from "@/layouts/app-layout"
import {
  adminReportsPath,
  businessesNewPath,
  eventsNewPath,
  interviewsNewPath,
  productsNewPath,
  raffleNewPath,
} from "@/routes"

interface AdminStats {
  users: {
    total: number
    new_this_month: number
    verified: number
    banned: number
  }
  events: {
    total: number
    active: number
    completed: number
    this_month: number
    attendees: number
  }
  marketplace: {
    total_products: number
    active_products: number
    total_sales: number
    revenue: number
  }
  forum: {
    total_posts: number
    total_answers: number
    active_discussions: number
    resolved: number
  }
  raffles: {
    total: number
    active: number
    completed: number
    total_participants: number
  }
  reports: {
    pending: number
    reviewed: number
    resolved: number
    dismissed: number
    critical: number
  }
  businesses: {
    total: number
    verified: number
    pending_verification: number
  }
  kpis: {
    dau: number
    wau: number
    mau: number
    retention_day_7: number
    retention_day_30: number
    posts_today: number
    posts_this_week: number
    avg_session_duration: number
    avg_session_duration_formatted: string
    avg_profile_completion: number
    onboarding_completion_rate: number
  }
}

interface RecentActivity {
  id: number
  type:
    | "user_registration"
    | "event_created"
    | "product_listed"
    | "forum_post"
    | "report_filed"
    | "raffle_created"
  description: string
  user?: {
    id: number
    name: string
  }
  created_at: string
}

interface PendingReview {
  id: number
  type:
    | "business_verification"
    | "reported_content"
    | "user_ban_appeal"
    | "event_approval"
  title: string
  priority: "low" | "medium" | "high" | "critical"
  created_at: string
  url: string
}

interface Props {
  stats: AdminStats
  recent_activities: RecentActivity[]
  pending_reviews: PendingReview[]
}

export default function AdminDashboard({
  stats,
  recent_activities,
  pending_reviews,
}: Props) {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("es-ES", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  const getActivityIcon = (type: string) => {
    switch (type) {
      case "user_registration":
        return <Users className="h-4 w-4" />
      case "event_created":
        return <Calendar className="h-4 w-4" />
      case "product_listed":
        return <ShoppingBag className="h-4 w-4" />
      case "forum_post":
        return <MessageSquare className="h-4 w-4" />
      case "report_filed":
        return <Flag className="h-4 w-4" />
      case "raffle_created":
        return <Trophy className="h-4 w-4" />
      default:
        return <Activity className="h-4 w-4" />
    }
  }

  return (
    <AppLayout>
      <Head title="Panel de Administración" />

      <div className="mx-auto max-w-7xl space-y-6 p-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Panel de Administración</h1>
            <p className="text-muted-foreground">Vista general del sistema</p>
          </div>
        </div>
        {/* Quick Create: contenido oficial */}
        <Card>
          <CardContent>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              <Link href={`${eventsNewPath()}?from=/admin`}>
                <Button variant="default" className="w-full justify-start">
                  <Calendar className="mr-2 h-4 w-4" />
                  Nuevo evento
                </Button>
              </Link>
              <Link href="/anuncios/new?from=/admin">
                <Button variant="default" className="w-full justify-start">
                  <Flag className="mr-2 h-4 w-4" />
                  Nuevo anuncio
                </Button>
              </Link>
              <Link href={`${interviewsNewPath()}?from=/admin`}>
                <Button variant="default" className="w-full justify-start">
                  <MessageSquare className="mr-2 h-4 w-4" />
                  Nueva entrevista
                </Button>
              </Link>
              <Link href={`${raffleNewPath()}?from=/admin`}>
                <Button variant="default" className="w-full justify-start">
                  <Trophy className="mr-2 h-4 w-4" />
                  Nuevo sorteo
                </Button>
              </Link>
              <Link href={`${businessesNewPath()}?from=/admin`}>
                <Button variant="default" className="w-full justify-start">
                  <Shield className="mr-2 h-4 w-4" />
                  Nuevo negocio
                </Button>
              </Link>
              <Link href={`${productsNewPath()}?from=/admin`}>
                <Button variant="default" className="w-full justify-start">
                  <ShoppingBag className="mr-2 h-4 w-4" />
                  Nuevo producto
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* KPIs Section - Usuarios Activos */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Users className="mr-2 h-5 w-5" />
              Usuarios Activos
            </CardTitle>
            <p className="text-muted-foreground text-sm">
              Usuarios únicos que han visitado la plataforma en cada período
            </p>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div className="rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-800 dark:bg-green-950">
                <div className="text-3xl font-bold text-green-700 dark:text-green-400">
                  {stats.kpis.dau}
                </div>
                <div className="text-sm font-medium text-green-600 dark:text-green-500">
                  Activos Hoy
                </div>
                <div className="text-muted-foreground mt-1 text-xs">
                  Usuarios que entraron hoy
                </div>
              </div>
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-950">
                <div className="text-3xl font-bold text-blue-700 dark:text-blue-400">
                  {stats.kpis.wau}
                </div>
                <div className="text-sm font-medium text-blue-600 dark:text-blue-500">
                  Activos Esta Semana
                </div>
                <div className="text-muted-foreground mt-1 text-xs">
                  Últimos 7 días
                </div>
              </div>
              <div className="rounded-lg border border-purple-200 bg-purple-50 p-4 dark:border-purple-800 dark:bg-purple-950">
                <div className="text-3xl font-bold text-purple-700 dark:text-purple-400">
                  {stats.kpis.mau}
                </div>
                <div className="text-sm font-medium text-purple-600 dark:text-purple-500">
                  Activos Este Mes
                </div>
                <div className="text-muted-foreground mt-1 text-xs">
                  Últimos 30 días
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* KPIs Section - Retención y Engagement */}
        <div className="grid gap-4 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Activity className="mr-2 h-5 w-5" />
                Retención de Usuarios
              </CardTitle>
              <p className="text-muted-foreground text-sm">
                % de usuarios que vuelven después de registrarse
              </p>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4 text-center">
                <div className="p-4 text-center">
                  <div className="text-3xl font-bold">
                    {stats.kpis.retention_day_7}%
                  </div>
                  <div className="text-sm font-medium">Retención 7 días</div>
                  <div className="text-muted-foreground mt-1 text-xs">
                    Vuelven después de 1 semana
                  </div>
                </div>
                <div className="p-4 text-center">
                  <div className="text-3xl font-bold">
                    {stats.kpis.retention_day_30}%
                  </div>
                  <div className="text-sm font-medium">Retención 30 días</div>
                  <div className="text-muted-foreground mt-1 text-xs">
                    Vuelven después de 1 mes
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Eye className="mr-2 h-5 w-5" />
                Engagement
              </CardTitle>
              <p className="text-muted-foreground text-sm">
                Tiempo y completitud de perfiles
              </p>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 text-center">
                  <div className="text-3xl font-bold">
                    {stats.kpis.avg_session_duration_formatted}
                  </div>
                  <div className="text-sm font-medium">Duración Promedio</div>
                  <div className="text-muted-foreground mt-1 text-xs">
                    Tiempo por sesión
                  </div>
                </div>
                <div className="p-4 text-center">
                  <div className="text-3xl font-bold">
                    {stats.kpis.avg_profile_completion}%
                  </div>
                  <div className="text-sm font-medium">Perfil Completado</div>
                  <div className="text-muted-foreground mt-1 text-xs">
                    Promedio de usuarios
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* KPIs Section - Actividad de Contenido */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <MessageSquare className="mr-2 h-5 w-5" />
              Actividad de Contenido
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
              <div className="p-4 text-center">
                <div className="text-3xl font-bold">
                  {stats.kpis.posts_today}
                </div>
                <div className="text-sm font-medium">Posts Hoy</div>
                <div className="text-muted-foreground mt-1 text-xs">
                  Publicaciones del foro
                </div>
              </div>
              <div className="p-4 text-center">
                <div className="text-3xl font-bold">
                  {stats.kpis.posts_this_week}
                </div>
                <div className="text-sm font-medium">Posts Esta Semana</div>
                <div className="text-muted-foreground mt-1 text-xs">
                  Últimos 7 días
                </div>
              </div>
              <div className="p-4 text-center">
                <div className="text-3xl font-bold">
                  {stats.kpis.onboarding_completion_rate}%
                </div>
                <div className="text-sm font-medium">Onboarding Completado</div>
                <div className="text-muted-foreground mt-1 text-xs">
                  Usuarios nuevos (30 días)
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Additional Stats */}
        <div className="grid gap-4 md:grid-cols-3">
          {/* Users */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Users className="mr-2 h-5 w-5" />
                Usuarios
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm">Usuarios totales:</span>
                <span className="font-medium">
                  {stats.users.total.toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm">Nuevos este mes:</span>
                <span className="font-medium">
                  +{stats.users.new_this_month}
                </span>
              </div>
              {stats.users.banned > 0 && (
                <div className="flex justify-between">
                  <span className="text-sm">Bloqueados:</span>
                  <span className="font-medium text-red-600">
                    {stats.users.banned}
                  </span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Events */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Calendar className="mr-2 h-5 w-5" />
                Eventos
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm">Eventos totales:</span>
                <span className="font-medium">{stats.events.total}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm">Asistentes:</span>
                <span className="font-medium">{stats.events.attendees}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm">Completados:</span>
                <span className="font-medium">{stats.events.completed}</span>
              </div>
            </CardContent>
          </Card>

          {/* Reports */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Flag className="mr-2 h-5 w-5" />
                Reportes
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm">Pendientes:</span>
                <span className="font-medium">
                  {stats.reports.pending || 0}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm">En revisión:</span>
                <span className="font-medium">
                  {stats.reports.reviewed || 0}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm">Resueltos:</span>
                <span className="font-medium">
                  {stats.reports.resolved || 0}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm">Descartados:</span>
                <span className="font-medium">
                  {stats.reports.dismissed || 0}
                </span>
              </div>
              <div className="pt-2 text-right">
                <Link
                  href={adminReportsPath()}
                  className="text-primary text-xs font-medium underline-offset-2 hover:underline"
                >
                  Ver reportes →
                </Link>
              </div>
            </CardContent>
          </Card>
          {/* Forum Stats */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <MessageSquare className="mr-2 h-5 w-5" />
                Foro
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm">Posts totales:</span>
                <span className="font-medium">{stats.forum.total_posts}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm">Respuestas:</span>
                <span className="font-medium">{stats.forum.total_answers}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm">Resueltos:</span>
                <span className="font-medium">{stats.forum.resolved}</span>
              </div>
            </CardContent>
          </Card>

          {/* Raffles Stats */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Trophy className="mr-2 h-5 w-5" />
                Sorteos
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm">Total:</span>
                <span className="font-medium">{stats.raffles.total}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm">Completados:</span>
                <span className="font-medium">{stats.raffles.completed}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm">Participantes:</span>
                <span className="font-medium">
                  {stats.raffles.total_participants}
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Negocios + Marketplace */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Shield className="mr-2 h-5 w-5" />
                Market
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm">Negocios totales:</span>
                <span className="font-medium">{stats.businesses.total}</span>
              </div>
              {stats.businesses.pending_verification > 0 && (
                <div className="flex justify-between">
                  <span className="text-sm">Negocios pendientes:</span>
                  <span className="font-medium text-orange-600">
                    {stats.businesses.pending_verification}
                  </span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-sm">Productos totales:</span>
                <span className="font-medium">
                  {stats.marketplace.total_products}
                </span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Activity */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Activity className="mr-2 h-5 w-5" />
                Actividad Reciente
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {recent_activities.map((activity) => (
                  <div
                    key={activity.id}
                    className="flex items-center space-x-3"
                  >
                    <div className="bg-muted rounded-full p-2">
                      {getActivityIcon(activity.type)}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm">{activity.description}</p>
                      {activity.user && (
                        <p className="text-muted-foreground text-xs">
                          por {activity.user.name}
                        </p>
                      )}
                    </div>
                    <div className="text-muted-foreground text-xs">
                      {formatDate(activity.created_at)}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
        {/* Revisiones pendientes */}
        {pending_reviews.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <AlertTriangle className="mr-2 h-5 w-5 text-orange-500" />
                Revisiones pendientes
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {pending_reviews.map((item) => (
                  <a
                    key={`${item.type}-${item.id}`}
                    href={item.url}
                    className="hover:bg-muted/50 flex items-center justify-between rounded-lg p-3 transition-colors"
                  >
                    <div>
                      <p className="text-sm font-medium">{item.title}</p>
                      <p className="text-muted-foreground text-xs">
                        {formatDate(item.created_at)}
                      </p>
                    </div>
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                        item.priority === "critical"
                          ? "bg-red-100 text-red-700"
                          : item.priority === "high"
                            ? "bg-orange-100 text-orange-700"
                            : "bg-yellow-100 text-yellow-700"
                      }`}
                    >
                      {item.priority === "critical"
                        ? "Crítico"
                        : item.priority === "high"
                          ? "Alto"
                          : "Medio"}
                    </span>
                  </a>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </AppLayout>
  )
}
